import { Box, Container, Typography, Button } from "@mui/material";
import { keyframes } from "@mui/system";
import { Link } from "react-router-dom";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50%       { transform: translateY(-10px) rotate(4deg); }
`;

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.5; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const shimmerBg = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const cards = [
  {
    title: "Ecommerce App",
    description: "Centralize clients, deals, and support with built-in automations, analytics, and a powerful admin dashboard.",
    rating: "4.9",
    tag: "Enterprise",
    emoji: "🛒",
    url: "/products/ecommerce-app",
    gradient: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
    glow: "rgba(91,124,250,0.3)",
    tagBg: "rgba(255,255,255,0.18)",
  },
  {
    title: "Sport Reserve",
    description: "Book and manage courts, coaches, and events with real-time availability and smart scheduling engine.",
    rating: "4.8",
    tag: "Sports",
    emoji: "🎾",
    url: "/products/sport-reserve",
    gradient: "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)",
    glow: "rgba(59,130,246,0.3)",
    tagBg: "rgba(255,255,255,0.18)",
  },
  {
    title: "Talent Flow",
    description: "ATS + onboarding + payroll workflows to hire fast, stay compliant, and scale your team with confidence.",
    rating: "4.9",
    tag: "People",
    emoji: "🧑‍💼",
    url: "/products/talent-flow",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)",
    glow: "rgba(139,92,246,0.3)",
    tagBg: "rgba(255,255,255,0.18)",
  },
  {
    title: "Queue Bridge",
    description: "Appointment and queue management with live wait times, smart routing, and seamless customer notifications.",
    rating: "4.7",
    tag: "Appointments",
    emoji: "📅",
    url: "/products/queue-bridge",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
    glow: "rgba(245,158,11,0.3)",
    tagBg: "rgba(255,255,255,0.18)",
  },
];

const ProductsSection = () => {
  return (
    <Box
      sx={{
        width: "100%",
        py: { xs: 8, sm: 10, md: 14 },
        background: "linear-gradient(160deg, #f8faff 0%, #eef3ff 50%, #f4f0ff 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Dot grid */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(circle, rgba(91,124,250,0.08) 1px, transparent 1px)",
          backgroundSize: "36px 36px",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 8, md: 10 } }}>
          {/* Pill */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              py: 0.8,
              borderRadius: "999px",
              border: "1px solid rgba(91,124,250,0.3)",
              background: "rgba(91,124,250,0.07)",
              mb: 3,
              animation: `${fadeUp} 0.6s ease-out both`,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #5b7cfa, #764ba2)",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  background: "#5b7cfa",
                  animation: `${pulseRing} 1.8s ease-out infinite`,
                },
              }}
            />
            <Typography sx={{ color: "#5b7cfa", fontSize: 12, fontWeight: 700, letterSpacing: 1.5 }}>
              WHAT WE BUILT
            </Typography>
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "30px", sm: "40px", md: "52px" },
              fontWeight: 800,
              letterSpacing: "-1px",
              lineHeight: 1.15,
              mb: 2.5,
              animation: `${fadeUp} 0.7s ease-out 0.1s both`,
              background: "linear-gradient(135deg, #1a1a2e 0%, #5b7cfa 50%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Popular Products
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "18px", md: "19px" },
              color: "#64748b",
              maxWidth: 600,
              mx: "auto",
              lineHeight: 1.8,
              animation: `${fadeUp} 0.7s ease-out 0.2s both`,
            }}
          >
            Premium solutions built for fast-growing teams — ready to deploy, easy to scale.
          </Typography>
        </Box>

        {/* Cards grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
            gap: { xs: 3, sm: 4 },
          }}
        >
          {cards.map((card, idx) => (
            <Box
              key={card.title}
              sx={{
                borderRadius: "24px",
                overflow: "hidden",
                background: "#fff",
                boxShadow: "0 4px 24px rgba(17,24,39,0.06)",
                border: "1px solid rgba(91,124,250,0.1)",
                transition: "transform 0.4s ease, box-shadow 0.4s ease",
                animation: `${fadeUp} 0.7s ease-out ${0.1 * idx}s both`,
                display: "flex",
                flexDirection: "column",
                "&:hover": {
                  transform: "translateY(-10px)",
                  boxShadow: `0 28px 60px ${card.glow}`,
                },
                "&:hover .card-emoji": {
                  animation: `${floatY} 2s ease-in-out infinite`,
                },
              }}
            >
              {/* Gradient header */}
              <Box
                sx={{
                  height: { xs: 190, sm: 210 },
                  background: card.gradient,
                  backgroundSize: "200% 200%",
                  animation: `${shimmerBg} 6s ease infinite`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Subtle pattern overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)",
                    backgroundSize: "20px 20px",
                    pointerEvents: "none",
                  }}
                />

                {/* Ring decorations */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -40,
                    right: -40,
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    border: "2px solid rgba(255,255,255,0.12)",
                    pointerEvents: "none",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -20,
                    right: -20,
                    width: 90,
                    height: 90,
                    borderRadius: "50%",
                    border: "2px solid rgba(255,255,255,0.08)",
                    pointerEvents: "none",
                  }}
                />

                {/* Tag pill */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 14,
                    left: 14,
                    px: 1.8,
                    py: 0.6,
                    borderRadius: "999px",
                    background: card.tagBg,
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                  }}
                >
                  {card.tag}
                </Box>

                {/* Star rating — top right */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.4,
                    px: 1.4,
                    py: 0.5,
                    borderRadius: "999px",
                    background: "rgba(0,0,0,0.22)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <Box component="span" sx={{ color: "#fbbf24", fontSize: 12, lineHeight: 1 }}>★</Box>
                  <Typography sx={{ color: "#fff", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>
                    {card.rating}
                  </Typography>
                </Box>

                {/* Emoji */}
                <Box
                  className="card-emoji"
                  sx={{
                    fontSize: { xs: 68, sm: 76 },
                    lineHeight: 1,
                    filter: "drop-shadow(0 12px 20px rgba(0,0,0,0.2))",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {card.emoji}
                </Box>
              </Box>

              {/* Card body */}
              <Box
                sx={{
                  p: { xs: "24px", sm: "28px" },
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "19px", sm: "21px" },
                    fontWeight: 800,
                    color: "#1f2937",
                    mb: 1,
                    letterSpacing: "-0.3px",
                  }}
                >
                  {card.title}
                </Typography>

                <Typography
                  sx={{
                    color: "#6b7280",
                    lineHeight: 1.7,
                    mb: 3,
                    fontSize: { xs: "14px", sm: "15px" },
                    flexGrow: 1,
                  }}
                >
                  {card.description}
                </Typography>

                <Button
                  component={Link}
                  to={card.url}
                  variant="outlined"
                  fullWidth
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "12px",
                    py: 1.3,
                    fontSize: 14,
                    borderColor: "rgba(91,124,250,0.3)",
                    color: "#5b7cfa",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#5b7cfa",
                      background: "linear-gradient(135deg, #5b7cfa, #764ba2)",
                      color: "#fff",
                      boxShadow: "0 8px 20px rgba(91,124,250,0.3)",
                    },
                  }}
                >
                  View Details →
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ProductsSection;
